// ============================================
// TRAVEL DESTINATION HINT EXTRACTION
// ============================================
// Extracted from VoiceBotPanel.tsx in Wave 1 of the widget refactor.
// Pure utilities used by the travel-RAG enrichment path. No React, no side effects.

import type { Message } from '../types'

const TRAVEL_GENERIC_QUERY_TERM_PATTERN = /^(link|send|show|please|chat|לינק|קישור|שלח|תשלחי|שלחו|תראה|תראי|אפשר|רוצה|הזה|הזאת|יש|עם|ל|לי|את|של|אני|בואו|ביקשתי|ישיר|הישיר|הקישור|המלון|למלון|מלון|מסעדה|אטרקציה|רכב|טיסה|בדובאי|בדוביי)$/i

const TRAVEL_CONTEXTUAL_QUERY_PATTERN = /(אי|איים|חופשה|רומנט|מלון|לינה|מסעד|אטרקצי|רכב|השכרת|טיסה|מזג|חוף|חופים|מומל|כדאי|לעשות|vacation|romantic|island|hotel|restaurant|attraction|car|rental|weather|beach)/i

const TRAVEL_DESTINATION_CANONICAL_HINTS = [
  {
    canonicalHint: 'יוון',
    aliases: [/יוון/u, /greece/i, /יווני/u, /כרתים/u, /crete/i, /קורפו/u, /corfu/i, /סנטוריני/u, /santorini/i, /מיקונוס/u, /רודוס/u, /rhodes/i, /אתונה/u, /athens/i, /מטאורה/u, /meteora/i, /חלקידיקי/u, /halkidiki/i, /פלופונס/u, /peloponnese/i, /סלוניקי/u, /thessaloniki/i, /אפירוס/u, /epirus/i, /parga/i, /פליון/u, /pelion/i, /נאקסוס/u, /naxos/i, /פארוס/u, /paros/i, /מילוס/u, /milos/i, /לפקדה/u, /lefkada/i, /לסבוס/u, /lesvos/i, /lesbos/i, /הידרה/u, /hydra/i, /קיתירה/u, /kythira/i, /אנדרוס/u, /andros/i, /אוויה/u, /evia/i, /euboea/i, /תסליה/u, /thessaly/i, /צפון יוון/u, /north.?greece/i, /נפקטוס/u, /nafplio/i, /kalamata/i, /olympus/i],
  },
  {
    canonicalHint: 'דובאי',
    aliases: [/דובאי/u, /dubai/i, /איחוד האמירויות/u, /uae/i, /אבו דאבי/u, /abu.?dhabi/i, /אמירויות/u, /emirates/i],
  },
  {
    canonicalHint: 'איטליה',
    aliases: [/איטליה/u, /italy/i, /רומא/u, /rome/i, /טוסקנה/u, /tuscany/i, /ונציה/u, /venice/i, /אמלפי/u, /amalfi/i, /גארדה/u, /garda/i, /אגם גארדה/u, /מילאנו/u, /milan/i, /סרדיניה/u, /sardinia/i, /סיציליה/u, /sicily/i, /ורונה/u, /verona/i, /נאפולי/u, /naples/i, /אמיליה/u, /emilia/i, /פארמה/u, /parma/i, /בולוניה/u, /bologna/i, /ריביירה/u, /riviera/i, /cinque.?terre/i, /דרום טירול/u, /south.?tyrol/i, /טירול/u],
  },
  {
    canonicalHint: 'קפריסין',
    aliases: [/קפריסין/u, /cyprus/i, /לרנקה/u, /larnaca/i, /ניקוסיה/u, /nicosia/i],
  },
  {
    canonicalHint: 'פורטוגל',
    aliases: [/פורטוגל/u, /portugal/i, /ליסבון/u, /lisbon/i, /פורטו/u, /porto/i],
  },
  {
    canonicalHint: 'ספרד',
    aliases: [/ספרד/u, /spain/i, /ברצלונה/u, /barcelona/i, /קאמפ נאו/u, /camp.?nou/i, /מדריד/u, /madrid/i],
  },
  {
    canonicalHint: 'ישראל',
    aliases: [/ישראל/u, /israel/i, /אילת/u, /eilat/i, /ירושלים/u, /jerusalem/i, /עין כרם/u, /ein.?karem/i, /חיפה/u, /haifa/i, /עכו/u, /acre/i, /akko/i, /כנרת/u, /kinneret/i, /ים המלח/u, /dead.?sea/i, /תל אביב/u, /tel.?aviv/i, /נגב/u, /negev/i, /עין גדי/u, /ein.?gedi/i, /תמנע/u, /timna/i, /עמק חפר/u, /ואדי ניסנאס/u, /אכזיבית/u, /קליה/u, /kalia/i, /דרום אדום/u],
  },
  {
    canonicalHint: 'תאילנד',
    aliases: [/תאילנד/u, /thailand/i, /קוסמוי/u, /koh.?samui/i, /קופנגן/u, /koh.?phangan/i, /בנגקוק/u, /bangkok/i, /פוקט/u, /phuket/i, /תאי/u],
  },
  {
    canonicalHint: 'הונגריה',
    aliases: [/הונגריה/u, /hungary/i, /בודפשט/u, /budapest/i],
  },
  {
    canonicalHint: 'בולגריה',
    aliases: [/בולגריה/u, /bulgaria/i, /בנסקו/u, /bansko/i],
  },
  {
    canonicalHint: 'גאורגיה',
    aliases: [/גאורגיה/u, /georgia/i, /טביליסי/u, /tbilisi/i, /בטומי/u, /batumi/i],
  },
  {
    canonicalHint: 'מונטנגרו',
    aliases: [/מונטנגרו/u, /montenegro/i, /קוטור/u, /kotor/i, /בודווה/u, /budva/i, /פרסט/u, /perast/i, /סבטי סטפן/u],
  },
  {
    canonicalHint: 'אוסטריה',
    aliases: [/אוסטריה/u, /austria/i, /וינה/u, /vienna/i, /vienna/i],
  },
  {
    canonicalHint: 'פולין',
    aliases: [/פולין/u, /poland/i, /ורשה/u, /warsaw/i],
  },
  {
    canonicalHint: 'צכיה',
    aliases: [/צ'כיה/u, /צכיה/u, /czech/i, /פראג/u, /prague/i],
  },
  {
    canonicalHint: 'לפלנד',
    aliases: [/לפלנד/u, /lapland/i, /פינלנד/u, /finland/i, /זוהר צפוני/u, /northern.?lights/i, /aurora/i],
  },
  {
    canonicalHint: 'ארהב',
    aliases: [/ארה"ב/u, /ארהב/u, /usa/i, /united.?states/i, /ניו יורק/u, /new.?york/i, /nyc/i, /מנהטן/u, /manhattan/i],
  },
] as const

const TRAVEL_DESTINATION_STOP_WORDS = new Set([
  'אחד', 'אחת', 'אחרי', 'איזה', 'איזו', 'איך', 'אני', 'אנחנו', 'אותו', 'אותה', 'את', 'אתה', 'אתם', 'אתן', 'בוא', 'בואי', 'בואו', 'גם',
  'הוא', 'היא', 'הזה', 'הזאת', 'הכי', 'היום', 'היינו', 'הייתי', 'ואז', 'ולחשוב', 'ולי', 'חופשה', 'חופש', 'חופים', 'חוף', 'כדאי', 'כיוון',
  'לגבי', 'לזוג', 'זוג', 'לחופשה', 'לחשוב', 'לינה', 'לינק', 'לישון', 'לי', 'מה', 'מזג', 'מומלץ', 'מומלצת', 'מלון', 'מלונות', 'מסעדה', 'מסעדות',
  'אפשר', 'עיר', 'ערים', 'עוד', 'עם', 'רומנטי', 'רומנטית', 'רוצה', 'רכב', 'שם', 'תכנון', 'תשלחי', 'תראה',
  'נמשיך', 'נחשוב', 'נדבר', 'טוב', 'טובה', 'יפה', 'יפים', 'שווה', 'משפחה', 'ילדים', 'ילד',
  'weather', 'best', 'beach',
  'beaches', 'book', 'booking', 'car', 'cars', 'chat', 'city', 'destination', 'direct', 'flight', 'flights', 'for', 'good', 'guide',
  'help', 'hotel', 'hotels', 'idea', 'in', 'island', 'islands', 'link', 'links', 'need', 'place', 'please', 'rental', 'restaurant',
  'restaurants', 'romantic', 'send', 'show', 'stay', 'there', 'trip', 'vacation', 'want', 'where'
])

const HEBREW_PREFIX_PATTERN = /^(?:ו|ה|ב|כ|ל|מ|ש){1,2}/

const stripHebrewPrefixes = (term: string) => {
  let normalized = term
  while (normalized.length >= 4 && HEBREW_PREFIX_PATTERN.test(normalized)) {
    normalized = normalized.replace(HEBREW_PREFIX_PATTERN, '')
  }
  return normalized
}

const splitTravelTerms = (text: string) => (`${text || ''}`.toLowerCase().match(/[\p{L}\p{N}]{3,}/gu) || [])

const isTravelDestinationTerm = (term: string) => {
  const normalizedTerm = stripHebrewPrefixes(term.toLowerCase())
  if (normalizedTerm.length < 3) return false
  if (TRAVEL_DESTINATION_STOP_WORDS.has(normalizedTerm)) return false
  if (TRAVEL_GENERIC_QUERY_TERM_PATTERN.test(normalizedTerm)) return false
  if (TRAVEL_CONTEXTUAL_QUERY_PATTERN.test(normalizedTerm)) return false
  return true
}

export const extractTravelDestinationHints = (text: string) => {
  const normalizedText = `${text || ''}`.toLowerCase()
  const canonicalHints = TRAVEL_DESTINATION_CANONICAL_HINTS
    .filter(({ aliases }) => aliases.some((alias) => alias.test(normalizedText)))
    .map(({ canonicalHint }) => canonicalHint)
  const genericHints = splitTravelTerms(normalizedText)
    .map((term) => stripHebrewPrefixes(term))
    .filter(isTravelDestinationTerm)

  return [...new Set([...canonicalHints, ...genericHints])]
}

export const collectTravelContextHints = (results: Array<{ metadata?: Record<string, unknown>; title?: string }>, query: string) => {
  const explicitHints = extractTravelDestinationHints(query)
  if (explicitHints.length > 0) return explicitHints

  const resultHints = results
    .slice(0, 3)
    .flatMap((result) => [
      result?.metadata?.country_he,
      result?.metadata?.country_en,
      result?.metadata?.location_he,
      result?.metadata?.location_en,
      result?.metadata?.region_he,
      result?.metadata?.region_en,
      result?.title,
    ])
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .flatMap((value) => value.split(/[\/|,]+/))
    .flatMap((value) => extractTravelDestinationHints(value))

  return [...new Set(resultHints)]
}

export const enrichTravelSearchQuery = (query: string, transcript: Message[], lastUserMessage: string, contextHints: string[]) => {
  const normalizedQuery = `${query || ''}`.trim()
  if (!normalizedQuery) return normalizedQuery
  if (extractTravelDestinationHints(normalizedQuery).length > 0) return normalizedQuery
  if (!TRAVEL_CONTEXTUAL_QUERY_PATTERN.test(normalizedQuery)) return normalizedQuery

  const recentUserMessages = [lastUserMessage, ...transcript.slice(-8).map((entry) => entry.role === 'user' ? entry.content : '')]
    .map((entry) => entry.trim())
    .filter(Boolean)

  const inferredHint = [...contextHints, ...recentUserMessages.flatMap((entry) => extractTravelDestinationHints(entry))][0]
  if (!inferredHint || normalizedQuery.includes(inferredHint)) return normalizedQuery

  return `${normalizedQuery} ${inferredHint}`
}
